// Időkapu-foglaló · Part 3 — Forwarder "Foglalásaim" page.
//
// Lists the caller's own bookings across all plants.
// Per-row actions:
//   - Módosítás (only if the plant's slot_config.modifiable_by_forwarder is true,
//     AND fields listed in modifiable_fields are still editable, AND slot is in future)
//   - Lemondás (only if slot is in future)
//
// The edit modal shows a note listing which fields are unlocked by the plant.

import React, { useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, CalendarClock, Pencil, X, Trash2, Save, AlertTriangle, Ticket } from "lucide-react";
import { toast } from "sonner";
import SlotTicket, { PrintButton } from "./DockSlotTicket";
import ArrivalPill from "./DockArrivalPill";
import BookingAudit from "./DockBookingAudit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dock-scheduling`;
const BLUE = "#185FA5";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});
const syne = (size, weight) => ({ fontFamily: "Syne, sans-serif", fontSize: size, fontWeight: weight || 700 });

const useMyBookings = (includePast) => useQuery({
  queryKey: ["ds-my-bookings", includePast],
  queryFn: () => axios.get(`${API}/my-bookings`, { params: { include_past: includePast } }).then(r => r.data.items || []),
});

function isFuture(iso) {
  if (!iso) return false;
  return new Date(iso) > new Date();
}

// ─── Edit modal ─────────────────────────────────────────────────────────────

function EditModal({ booking, onClose }) {
  const qc = useQueryClient();
  const { data: meta } = useQuery({
    queryKey: ["ds-plant-meta", booking.tenant_id],
    queryFn: () => axios.get(`${API}/plants/${booking.tenant_id}/meta`).then(r => r.data),
    enabled: !!booking.tenant_id,
  });
  const cfg = meta?.slot_config;
  const modifiableFields = useMemo(() => new Set(cfg?.modifiable_fields || []), [cfg]);
  const editableDefs = useMemo(() => (meta?.field_defs || []).filter(f => modifiableFields.has(f.field_key)), [meta, modifiableFields]);

  const [values, setValues] = useState({ ...(booking.field_values || {}) });

  const save = useMutation({
    mutationFn: () => {
      // Only send the keys the plant explicitly unlocked.
      const fv = {};
      for (const k of modifiableFields) if (k in values) fv[k] = values[k];
      return axios.patch(`${API}/bookings/${booking.id}`, { field_values: fv }).then(r => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ds-my-bookings"] });
      toast.success("Foglalás módosítva");
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba a módosításkor"),
  });

  const enabled = cfg?.modifiable_by_forwarder;

  return (
    <div
      data-testid="ds-mine-edit-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-elevated)", color: "var(--text)",
          border: `1px solid ${BLUE}`, borderRadius: 8, padding: 18, maxWidth: 560, width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        }}
      >
        <div className="flex items-baseline gap-3 mb-3">
          <div style={syne(15, 800)}>Foglalás módosítása</div>
          <div className="flex-1" />
          <button type="button" onClick={onClose} data-testid="ds-mine-edit-close"
            className="p-1 rounded" style={{ background: "transparent", border: 0, cursor: "pointer", color: "var(--dimmer)" }}
          ><X size={16} /></button>
        </div>
        <div className="mb-3" style={mono(10, "var(--dimmer, #9198b0)")}>
          Időpont: <b style={{ color: "var(--text)" }}>{booking.slot_start_local}–{booking.slot_end_local}</b>
        </div>

        {!enabled && (
          <div className="rounded p-2 mb-3" style={{ background: "rgba(243,177,74,0.10)", border: "1px solid rgba(243,177,74,0.30)", ...mono(11, "#f3b14a") }}>
            <AlertTriangle size={12} style={{ display: "inline", verticalAlign: -2, marginRight: 6 }} />
            A gyár nem engedélyezi a fuvarozói módosítást.
          </div>
        )}
        {enabled && editableDefs.length === 0 && (
          <div style={mono(11, "var(--dimmer)")}>Egy módosítható mező sincs engedélyezve.</div>
        )}

        {enabled && editableDefs.length > 0 && (
          <>
            <div className="mb-2" style={mono(9, "var(--dimmer)", 700, "0.4px")}>MÓDOSÍTHATÓ MEZŐK</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {editableDefs.map(def => (
                <label key={def.field_key} className="block">
                  <div style={mono(9, "var(--dimmer)", 700, "0.4px")}>{def.label_hu.toUpperCase()}</div>
                  <input
                    data-testid={`ds-mine-edit-${def.field_key}`}
                    value={values[def.field_key] ?? ""}
                    onChange={(e) => setValues(s => ({ ...s, [def.field_key]: e.target.value }))}
                    type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"}
                    className="px-2 py-1.5 rounded w-full mt-1"
                    style={{ background: "var(--bg-input, #0a0b12)", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", ...mono(12) }}
                  />
                </label>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <div className="flex-1" />
              <button type="button" onClick={onClose}
                className="px-3 py-1.5 rounded"
                style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", cursor: "pointer", ...mono(11, undefined, 700, "0.4px") }}
              >Mégse</button>
              <button
                type="button"
                onClick={() => save.mutate()}
                data-testid="ds-mine-edit-save"
                disabled={save.isPending}
                className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
                style={{ background: BLUE, color: "#fff", border: 0, cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
              ><Save size={14} /> Mentés</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Ticket modal (with audit) ──────────────────────────────────────────────

function TicketModal({ booking, onClose }) {
  return (
    <div
      data-testid="ds-mine-ticket-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: "100%", margin: "auto" }}>
        <SlotTicket booking={booking} plantName={booking.plant_name} />
        <BookingAudit bookingId={booking.id} />
        <div className="mt-3 flex items-center gap-2 justify-center">
          <PrintButton />
          <button type="button" onClick={onClose}
            data-testid="ds-mine-ticket-close"
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.30)", color: "#fff", cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
          >Bezárás</button>
        </div>
      </div>
    </div>
  );
}


// ─── Cancel scope modal (this occurrence vs whole series) ──────────────────

function CancelScopeModal({ booking, onCancelOne, onCancelSeries, onClose }) {
  return (
    <div
      data-testid="ds-mine-cancel-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 65,
        background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-elevated)", color: "var(--text)",
          border: `1px solid ${BLUE}`, borderRadius: 8, padding: 18, maxWidth: 440, width: "100%",
        }}
      >
        <div className="flex items-baseline gap-3 mb-2">
          <div style={syne(15, 800)}>Foglalás lemondása</div>
          <div className="flex-1" />
          <button type="button" onClick={onClose}
            style={{ background: "transparent", border: 0, cursor: "pointer", color: "var(--dimmer)" }}
          ><X size={16} /></button>
        </div>
        <div className="mb-3" style={mono(11, "var(--dimmer)")}>
          Ez a foglalás egy ismétlődő sorozathoz tartozik. Csak ezt az alkalmat vagy a teljes sorozatot mondja le?
        </div>
        <div className="mb-3" style={mono(10, "var(--dimmer)")}>
          <b style={{ color: "var(--text)" }}>{booking.reference}</b>
          {" · "}
          {(booking.slot_start || "").slice(0, 10)} {booking.slot_start_local}
        </div>
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(148,163,184,0.30)", color: "var(--text)", cursor: "pointer", ...mono(11, undefined, 700, "0.4px") }}
          >Mégse</button>
          <button
            type="button"
            data-testid="ds-cancel-scope-one"
            onClick={onCancelOne}
            className="px-3 py-1.5 rounded"
            style={{ background: "rgba(248,113,113,0.14)", border: "1px solid rgba(248,113,113,0.45)", color: "#f87171", cursor: "pointer", ...mono(11, "#f87171", 700, "0.4px") }}
          >Csak ez</button>
          <button
            type="button"
            data-testid="ds-cancel-scope-series"
            onClick={onCancelSeries}
            className="px-3 py-1.5 rounded"
            style={{ background: "#f87171", border: 0, color: "#fff", cursor: "pointer", ...mono(11, "#fff", 700, "0.4px") }}
          >Az egész sorozat (jövőbeli)</button>
        </div>
      </div>
    </div>
  );
}


// ─── Page ───────────────────────────────────────────────────────────────────

export default function DockMyBookings() {
  const qc = useQueryClient();
  const [includePast, setIncludePast] = useState(false);
  const { data: bookings = [], isLoading } = useMyBookings(includePast);
  const [editing, setEditing] = useState(null);
  const [ticket, setTicket] = useState(null);  // iter146 · slot-ticket modal
  const [cancelling, setCancelling] = useState(null);  // iter147 · cancel-scope modal

  const cancel = useMutation({
    mutationFn: ({ id, scope }) => axios.post(`${API}/bookings/${id}/cancel`, null, { params: scope ? { scope } : {} }).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["ds-my-bookings"] });
      const n = (data?.series_cancelled || []).length;
      toast.success(n > 1 ? `Sorozat lemondva (${n} időpont)` : "Foglalás lemondva");
      setCancelling(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Hiba a lemondáskor"),
  });

  return (
    <div data-testid="ds-mine" className="p-1">
      <header className="mb-4 flex items-center gap-3">
        <div>
          <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 30px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
            <CalendarClock size={26} color={BLUE} style={{ display: "inline", verticalAlign: -4, marginRight: 10 }} />
            Foglalásaim
          </h1>
          <div className="mt-1" style={mono(11, "var(--text-dim, #c5cad8)")}>
            Fuvarozói időkapu-foglalások · összes gyár
          </div>
        </div>
        <div className="flex-1" />
        <label className="flex items-center gap-2 cursor-pointer" style={mono(10, "var(--dimmer)")}>
          <input type="checkbox" checked={includePast} onChange={(e) => setIncludePast(e.target.checked)} data-testid="ds-mine-past-toggle" />
          Múltbeli foglalások is
        </label>
        <Link to="/dock-scheduling/book" data-testid="ds-mine-new-btn"
          className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
          style={{ background: BLUE, color: "#fff", textDecoration: "none", ...mono(11, "#fff", 700, "0.4px") }}
        ><Plus size={14} /> Új foglalás</Link>
      </header>

      {isLoading && <div style={mono(11, "var(--dimmer)")}>Betöltés…</div>}
      {!isLoading && bookings.length === 0 && (
        <div className="rounded p-4" style={{ background: "var(--bg-surface)", border: "1px solid rgba(148,163,184,0.15)", ...mono(12, "var(--dimmer)") }}>
          Nincs foglalásod. Kattints az „Új foglalás” gombra a rögzítéshez.
        </div>
      )}

      {!isLoading && bookings.length > 0 && (
        <div className="rounded-md" style={{ background: "var(--bg-surface)", border: "1px solid rgba(148,163,184,0.15)" }}>
          <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(148,163,184,0.20)" }}>
                {["IDŐPONT", "REFERENCIA", "GYÁR", "VONAL", "SZOLGÁLTATÓ", "RENDSZÁM", "IRÁNY", "ÁLLAPOT", "ÉRKEZÉS", ""].map(h => (
                  <th key={h} className="text-left px-3 py-2" style={mono(10, "var(--dimmer)", 700, "0.4px")}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.map(b => {
                const future = isFuture(b.slot_end);
                const cancelled = b.status === "cancelled";
                return (
                  <tr key={b.id} data-testid={`ds-mine-row-${b.id}`}
                    style={{ borderBottom: "1px solid rgba(148,163,184,0.10)", opacity: cancelled ? 0.55 : 1 }}
                  >
                    <td className="px-3 py-2">
                      <div style={mono(12, undefined, 700)}>
                        {b.slot_start?.slice(0, 10)}
                      </div>
                      <div style={mono(11, "var(--blue, #185FA5)", 700, "0.4px")}>
                        {b.slot_start_local}–{b.slot_end_local}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        data-testid={`ds-mine-ref-${b.id}`}
                        onClick={() => setTicket(b)}
                        title="Slot-jegy megnyitása"
                        style={{
                          padding: "2px 8px", borderRadius: 4, cursor: "pointer",
                          background: "rgba(24,95,165,0.10)", border: "1px solid rgba(24,95,165,0.30)",
                          ...mono(11, "var(--blue, #185FA5)", 800, "0.6px"),
                        }}
                      >{b.reference || "—"}</button>
                    </td>
                    <td className="px-3 py-2" data-testid={`ds-mine-plant-${b.id}`}>
                      <span
                        style={{
                          padding: "2px 8px", borderRadius: 4,
                          background: "rgba(24,95,165,0.10)", border: "1px solid rgba(24,95,165,0.30)",
                          ...mono(11, "var(--blue, #185FA5)", 700, "0.3px"),
                        }}
                      >{b.plant_name || "—"}</span>
                    </td>
                    <td className="px-3 py-2" style={mono(12)}>{b.vonal || "—"}</td>
                    <td className="px-3 py-2" style={mono(12)}>{b.szolgaltato_neve || "—"}</td>
                    <td className="px-3 py-2" style={mono(12)}>{b.rendszam || "—"}</td>
                    <td className="px-3 py-2" style={mono(12)}>{b.direction === "inbound" ? "Bejövő" : b.direction === "outbound" ? "Kimenő" : "—"}</td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`ds-mine-status-${b.id}`}
                        style={{
                          padding: "2px 8px", borderRadius: 999,
                          ...mono(9, cancelled ? "#f87171" : "#22c55e", 700, "0.4px"),
                          background: cancelled ? "rgba(248,113,113,0.10)" : "rgba(34,197,94,0.10)",
                          border: `1px solid ${cancelled ? "rgba(248,113,113,0.35)" : "rgba(34,197,94,0.35)"}`,
                          textTransform: "uppercase",
                        }}
                      >{cancelled ? "Lemondva" : "Foglalt"}</span>
                    </td>
                    <td className="px-3 py-2">
                      <ArrivalPill status={b.arrival_status} testId={`ds-mine-arrival-${b.id}`} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setTicket(b)}
                          data-testid={`ds-mine-ticket-${b.id}`}
                          className="p-1 rounded"
                          title="Slot-jegy"
                          style={{ background: "transparent", border: 0, color: BLUE, cursor: "pointer" }}
                        ><Ticket size={13} /></button>
                        {!cancelled && future && (
                          <>
                            <button
                              type="button"
                              onClick={() => setEditing(b)}
                              data-testid={`ds-mine-edit-${b.id}`}
                              className="p-1 rounded"
                              title="Módosítás"
                              style={{ background: "transparent", border: 0, color: BLUE, cursor: "pointer" }}
                            ><Pencil size={13} /></button>
                            <button
                              type="button"
                              onClick={() => {
                                if (b.series_id) {
                                  setCancelling(b);
                                } else if (window.confirm("Biztosan lemondja ezt a foglalást?")) {
                                  cancel.mutate({ id: b.id });
                                }
                              }}
                              data-testid={`ds-mine-cancel-${b.id}`}
                              className="p-1 rounded"
                              title="Lemondás"
                              style={{ background: "transparent", border: 0, color: "#f87171", cursor: "pointer" }}
                            ><Trash2 size={13} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && <EditModal booking={editing} onClose={() => setEditing(null)} />}
      {ticket && <TicketModal booking={ticket} onClose={() => setTicket(null)} />}
      {cancelling && (
        <CancelScopeModal
          booking={cancelling}
          onCancelOne={() => cancel.mutate({ id: cancelling.id, scope: "this" })}
          onCancelSeries={() => cancel.mutate({ id: cancelling.id, scope: "series" })}
          onClose={() => setCancelling(null)}
        />
      )}
    </div>
  );
}
